/**
 * ToastHost — renders whatever src/lib/toast.js is holding. Card P219 item 8.
 *
 * ⛔ MOUNTED ONCE, ABOVE EVERYTHING. It is deliberately not a child of any
 * dialog: the notification has to survive the dialog that raised it, which is
 * the entire requirement ("you can keep working").
 *
 * ⚠ A success toast auto-dismisses; a working or error toast does NOT. "Still
 * generating" that vanishes after four seconds is worse than no toast, because
 * the reader is left exactly where they started and now distrusts the system.
 */

import React, { useEffect, useState } from 'react';
import { subscribeToasts, dismissToast } from '../lib/toast';
import { C, S, FONT } from '../pages/reports/_overviewTheme';
import { Spinner, prefersReducedMotion } from '../pages/reports/_reportProgress';

const TONE = {
  working: { border: C.border,  accent: C.primary, label: 'Working' },
  success: { border: '#bbf7d0', accent: '#15803D',  label: 'Done' },
  error:   { border: '#fecaca', accent: C.high,     label: 'Problem' },
};

const AUTO_DISMISS_MS = 8000;

export default function ToastHost() {
  const [items, setItems] = useState([]);
  const reduced = prefersReducedMotion();

  useEffect(() => subscribeToasts(setItems), []);

  useEffect(() => {
    const timers = items
      .filter((t) => !t.sticky)
      .map((t) => setTimeout(() => dismissToast(t.key), AUTO_DISMISS_MS));
    return () => timers.forEach(clearTimeout);
  }, [items]);

  if (items.length === 0) return null;

  return (
    <div
      data-testid="toast-host"
      /*
       * ⚠ polite, never assertive. These announce background work finishing;
       * interrupting whatever the reader is doing to say so is the behaviour
       * that makes people turn notifications off.
       */
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed', top: 16, right: 16, zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 10,
        maxWidth: 'min(380px, calc(100vw - 32px))', fontFamily: FONT,
      }}
    >
      {items.map((t) => {
        const tone = TONE[t.tone] || TONE.working;
        return (
          <div key={t.key} data-testid={`toast-${t.key}`} data-tone={t.tone}
               style={{ ...S.card, padding: 12, border: `1px solid ${tone.border}`,
                        background: '#fff', boxShadow: '0 6px 20px rgba(15,23,42,0.12)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              {t.tone === 'working' ? <Spinner reduced={reduced} size={12} /> : null}
              <strong style={{ fontSize: 13, color: C.text, flex: 1 }}>{t.title}</strong>
              <button type="button" data-testid={`toast-dismiss-${t.key}`}
                      aria-label="Dismiss notification"
                      onClick={() => dismissToast(t.key)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer',
                               color: C.faint, fontSize: 14, lineHeight: 1, padding: 0 }}>
                ×
              </button>
            </div>
            {t.body ? (
              <div style={{ fontSize: 12, color: C.body, marginTop: 4, lineHeight: 1.5 }}>
                {t.body}
              </div>
            ) : null}
            {Array.isArray(t.actions) && t.actions.length ? (
              <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                {t.actions.map((a) => (
                  <button key={a.label} type="button" data-testid={a.testId}
                          onClick={a.onClick}
                          style={{ background: 'none', border: 'none', padding: 0,
                                   font: 'inherit', fontSize: 12, fontWeight: 600,
                                   color: tone.accent, textDecoration: 'underline',
                                   cursor: 'pointer' }}>
                    {a.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
